// src/services/stripeService.js
import { loadStripe } from '@stripe/stripe-js';

class StripeService {
  constructor() {
    this.stripePromise = null;
  }

  /**
   * Ensure Stripe.js is initialized with the publishable key
   */
  async ensureStripe() {
    if (!this.stripePromise) {
      const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

      if (!publishableKey) {
        throw new Error('Stripe publishable key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.');
      }

      this.stripePromise = loadStripe(publishableKey);
    }

    return this.stripePromise;
  }

  /**
   * Create a Stripe customer
   */
  async createCustomer(customerData) {
    try {
      const response = await fetch('/api/stripe/create-customer', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerData),
      });

      if (!response.ok) {
        throw new Error('Failed to create customer');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating Stripe customer:', error);
      throw error;
    }
  }

  /**
   * Create a payment intent for subscription
   */
  async createPaymentIntent(subscriptionData) {
    try {
      const response = await fetch('/api/stripe/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating payment intent:', error);
      throw error;
    }
  }

  /**
   * Create a subscription with Stripe
   */
  async createSubscription(subscriptionData) {
    try {
      const response = await fetch('/api/stripe/create-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscriptionData),
      });

      if (!response.ok) {
        throw new Error('Failed to create subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  /**
   * Create a Stripe Checkout Session for subscriptions
   */
  async createCheckoutSession(sessionData) {
    try {
      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to create checkout session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Update a subscription
   */
  async updateSubscription(subscriptionId, updateData) {
    try {
      const response = await fetch(`/api/stripe/update-subscription/${subscriptionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error('Failed to update subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Cancel a subscription
   */
  async cancelSubscription(subscriptionId) {
    try {
      const response = await fetch(`/api/stripe/cancel-subscription/${subscriptionId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  /**
   * Get customer's payment methods
   */
  async getPaymentMethods(customerId) {
    try {
      const response = await fetch(`/api/stripe_api/payment-methods/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get payment methods');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting payment methods:', error);
      throw error;
    }
  }

  /**
   * Add a payment method to customer
   */
  async addPaymentMethod(customerId, paymentMethodData) {
    try {
      const response = await fetch(`/api/stripe_api/add-payment-method/${customerId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentMethodData),
      });

      if (!response.ok) {
        throw new Error('Failed to add payment method');
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    }
  }

  /**
   * Create setup intent for adding payment method
   */
  async createSetupIntent(customerId) {
    try {
      const response = await fetch(`/api/stripe_api/create-setup-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      if (!response.ok) {
        throw new Error('Failed to create setup intent');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error;
    }
  }

  /**
   * Delete a payment method
   */
  async deletePaymentMethod(paymentMethodId) {
    try {
      const response = await fetch(`/api/stripe_api/delete-payment-method/${paymentMethodId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete payment method');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    }
  }

  /**
   * Set default payment method
   */
  async setDefaultPaymentMethod(customerId, paymentMethodId) {
    try {
      const response = await fetch(`/api/stripe_api/set-default-payment-method/${customerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ paymentMethodId }),
      });

      if (!response.ok) {
        throw new Error('Failed to set default payment method');
      }

      return await response.json();
    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw error;
    }
  }

  /**
   * Validate promo code
   */
  async validatePromoCode(code, planId) {
    try {
      const response = await fetch('/api/stripe/validate-promo-code', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code, planId }),
      });

      if (!response.ok) {
        throw new Error('Failed to validate promo code');
      }

      return await response.json();
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  }

  /**
   * Get customer's subscription
   */
  async getCustomerSubscription(customerId) {
    try {
      const response = await fetch(`/api/stripe_api/customer-subscription/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get customer subscription');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting customer subscription:', error);
      throw error;
    }
  }

  /**
   * Get Stripe customer ID for current user
   */
  async getCustomerId(userId) {
    try {
      const { supabase } = await import('../config');
      
      // Query subscriptions table - use maybeSingle to handle no results
      const { data, error } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('user_id', userId)
        .maybeSingle();

      // Handle errors gracefully - don't break the app if subscription lookup fails
      if (error) {
        // PGRST116 means no rows found, which is fine
        if (error.code === 'PGRST116') {
          return null;
        }
        // Network errors, 500 errors, or RLS issues - log but don't throw
        // This allows the invoice page to still load even if subscription data isn't available
        console.warn('Could not fetch customer ID from subscriptions table:', {
          code: error.code,
          message: error.message,
          hint: error.hint
        });
        return null;
      }

      // Return customer ID if found - validate it's just the ID string
      let customerId = data?.stripe_customer_id;
      if (!customerId) return null;
      
      // Handle case where database might have stored full customer object
      if (typeof customerId === 'object') {
        customerId = customerId.id || customerId;
      } else if (typeof customerId === 'string' && customerId.startsWith('{')) {
        // It's a JSON string of customer object
        try {
          const customerObj = JSON.parse(customerId);
          customerId = customerObj.id || customerId;
        } catch {
          // If parsing fails, use as is
        }
      }
      
      // Ensure it's a string and starts with 'cus_'
      if (typeof customerId === 'string' && customerId.startsWith('cus_')) {
        return customerId;
      }
      
      return null;
    } catch (error) {
      // Catch network errors (ERR_FAILED) and other exceptions
      if (error.message?.includes('Failed to fetch') || error.message?.includes('ERR_FAILED')) {
        console.warn('Network error fetching customer ID. This may be due to RLS policies or network issues.');
      } else {
        console.error('Error getting customer ID:', error);
      }
      return null;
    }
  }

  /**
   * Get billing history
   */
  async getBillingHistory(customerId) {
    try {
      const response = await fetch(`/api/stripe_api/billing-history/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get billing history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting billing history:', error);
      throw error;
    }
  }

  /**
   * List invoices for a customer
   */
  async listInvoices(customerId) {
    try {
      const response = await fetch(`/api/stripe_api/invoices/list?customer_id=${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to list invoices');
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing invoices:', error);
      throw error;
    }
  }

  /**
   * Get a specific invoice
   */
  async getInvoice(invoiceId) {
    try {
      const response = await fetch(`/api/stripe_api/invoices/${invoiceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        const errorMessage = responseData.error || `Failed to get invoice (${response.status})`;
        throw new Error(errorMessage);
      }

      return responseData;
    } catch (error) {
      console.error('Error getting invoice:', error);
      throw error;
    }
  }

  /**
   * Get invoice PDF download URL
   */
  async getInvoicePdf(invoiceId) {
    try {
      const response = await fetch(`/api/stripe_api/invoices/download/${invoiceId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get invoice PDF');
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting invoice PDF:', error);
      throw error;
    }
  }

  /**
   * Retrieve checkout session details after redirect
   */
  async getCheckoutSession(sessionId, userId) {
    try {
      const query = userId ? `?user_id=${encodeURIComponent(userId)}` : '';
      const response = await fetch(`/api/stripe/checkout-session/${sessionId}${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to retrieve checkout session');
      }

      return await response.json();
    } catch (error) {
      console.error('Error retrieving checkout session:', error);
      throw error;
    }
  }

  /**
   * Process payment with Stripe Elements
   */
  async processPayment(paymentMethodId, amount, currency = 'usd') {
    try {
      const stripe = await this.ensureStripe();
      
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        paymentMethodId,
        {
          payment_method: paymentMethodId,
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      return paymentIntent;
    } catch (error) {
      console.error('Error processing payment:', error);
      throw error;
    }
  }

  /**
   * Setup payment method with Stripe Elements
   */
  async setupPaymentMethod(paymentMethodId) {
    try {
      const stripe = await this.ensureStripe();
      
      const { error, setupIntent } = await stripe.confirmCardSetup(
        paymentMethodId,
        {
          payment_method: paymentMethodId,
        }
      );

      if (error) {
        throw new Error(error.message);
      }

      return setupIntent;
    } catch (error) {
      console.error('Error setting up payment method:', error);
      throw error;
    }
  }

  /**
   * Get Stripe instance
   */
  async getStripe() {
    return await this.ensureStripe();
  }

  /**
   * Create a one-off invoice
   */
  async createInvoice(invoiceData) {
    try {
      // Log what we're sending for debugging
      console.log('🔍 Creating invoice with data:', {
        ...invoiceData,
        line_items: invoiceData.line_items?.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_amount: item.unit_amount,
          currency: item.currency
        }))
      });
      
      const response = await fetch('/api/stripe_api/create-invoice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invoiceData),
      });

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        // Log full error details
        console.error('❌ Invoice creation failed:', {
          status: response.status,
          error: responseData.error,
          debug: responseData.debug
        });
        
        // Build detailed error message
        let errorMessage = responseData.error || 'Failed to create invoice';
        if (responseData.debug) {
          errorMessage += '\n\nDebug info available in console (check Network tab → Response)';
          if (responseData.debug.stripe_error) {
            errorMessage += `\nStripe error: ${JSON.stringify(responseData.debug.stripe_error, null, 2)}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      console.log('✅ Invoice created successfully:', {
        success: responseData.success,
        invoice_id: responseData.invoice?.id,
        invoice_number: responseData.invoice?.invoice_number,
        status: responseData.invoice?.status,
        total: responseData.invoice?.total,
        amount_due: responseData.invoice?.amount_due,
        customer_id: responseData.invoice?.customer?.id || responseData.invoice?.customer_id,
        full_response: responseData
      });
      return responseData;
    } catch (error) {
      console.error('❌ Error creating invoice:', error);
      throw error;
    }
  }

  /**
   * Finalize a draft invoice
   */
  async finalizeInvoice(invoiceId, options = {}) {
    try {
      const response = await fetch(`/api/stripe_api/finalize-invoice/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to finalize invoice');
      }

      return await response.json();
    } catch (error) {
      console.error('Error finalizing invoice:', error);
      throw error;
    }
  }

  /**
   * Send an invoice to customer
   */
  async sendInvoice(invoiceId) {
    try {
      const response = await fetch(`/api/stripe_api/send-invoice/${invoiceId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to send invoice');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending invoice:', error);
      throw error;
    }
  }

  /**
   * List all draft invoices
   */
  async listDraftInvoices() {
    try {
      const response = await fetch('/api/stripe_api/invoices/drafts', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to get draft invoices' }));
        throw new Error(errorData.error || 'Failed to get draft invoices');
      }

      return await response.json();
    } catch (error) {
      console.error('Error listing draft invoices:', error);
      throw error;
    }
  }

  /**
   * Update a draft invoice
   */
  async updateInvoice(invoiceId, updateData) {
    try {
      const response = await fetch(`/api/stripe_api/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to update invoice');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating invoice:', error);
      throw error;
    }
  }

  /**
   * Delete/void an invoice
   */
  async deleteInvoice(invoiceId) {
    try {
      const response = await fetch(`/api/stripe_api/invoices/${invoiceId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody.error || 'Failed to delete invoice');
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting invoice:', error);
      throw error;
    }
  }
}

// Create and export singleton instance
const stripeService = new StripeService();
export default stripeService;










